import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Reveal from "@/components/base/Reveal";
import ScreeningCalendar from "@/pages/screenings/components/ScreeningCalendar";
import ScreeningList from "@/pages/screenings/components/ScreeningList";
import { screenings } from "@/mocks/screenings";
import { films } from "@/mocks/films";
import type { Film, Screening } from "@/types/content";

const allScreenings = screenings as Screening[];
const allFilms = films as Film[];

export default function Screenings() {
  usePageMeta(
    "放映动态-球幕影院场次安排与预约",
    "查看球幕影院近期放映场次安排，按日期与影片类型筛选，了解当日推荐场次、放映厅与票价信息。",
    "球幕影院放映动态,场次安排,穹顶影厅,影片排期,观影预约",
  );
  const dates = useMemo(() => Array.from(new Set(allScreenings.map((s) => s.date))).sort(), []);
  const [selected, setSelected] = useState(dates[0] ?? "2026-10-13");
  const [genre, setGenre] = useState("全部");

  const initialMonth = useMemo(() => {
    const base = new Date(selected);
    return { year: base.getFullYear(), month: base.getMonth() };
  }, [selected]);

  const [monthOffset, setMonthOffset] = useState(0);
  const year = initialMonth.year;
  const month = initialMonth.month + monthOffset;

  const genreOptions = useMemo(() => {
    const set = new Set<string>();
    allScreenings.forEach((s) => {
      const film = allFilms.find((f) => f.id === s.filmId);
      film?.genres.forEach((g) => set.add(g));
    });
    return ["全部", ...Array.from(set)];
  }, []);

  const list = allScreenings.filter((s) => {
    if (s.date !== selected) return false;
    if (genre === "全部") return true;
    const film = allFilms.find((f) => f.id === s.filmId);
    return film?.genres.includes(genre);
  });

  const today = "2026-10-13";
  const todayFeatured = allScreenings.filter((s) => s.date === today);

  return (
    <div className="w-full pt-20">
      <section className="bg-background-100/60 border-b border-background-200/60">
        <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">放映动态</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">放映动态</h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            选择日期查看当日穹顶厅场次安排。以下场次信息为演示示例数据，实际排期请以现场公示或票务系统为准。
          </p>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <Reveal>
          <div className="p-6 rounded-lg bg-primary-500 text-background-50 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-label bg-background-50/20 px-3 py-1 rounded-full mb-3">
                <i className="ri-flashlight-line" />
                今日推荐场次
              </span>
              <h2 className="font-heading font-bold text-xl md:text-2xl">
                10 月 13 日 · 共 {todayFeatured.length} 场沉浸放映
              </h2>
              <p className="text-sm text-background-50/80 mt-2">
                晚场《量子迷航》激光 8K 专场余票有限，建议提前预约。
              </p>
            </div>
            <Link
              to="/films/quantum-drift"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-background-50 font-label font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start md:self-auto"
            >
              <i className="ri-ticket-2-line" />
              查看推荐影片
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-8">
          <div className="lg:sticky lg:top-24 h-fit">
            <ScreeningCalendar
              year={year}
              month={month}
              availableDates={dates}
              selected={selected}
              onSelect={setSelected}
              onMonthChange={(delta) => setMonthOffset((prev) => prev + delta)}
            />
          </div>

          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-950">
                {selected} 场次安排
                <span className="text-sm font-normal text-foreground-500 ml-2">共 {list.length} 场</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                {genreOptions.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGenre(g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                      genre === g
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 border border-background-200/70 text-foreground-700 hover:text-foreground-950"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <ScreeningList items={list} />

            <p className="text-xs text-foreground-500 mt-8 leading-relaxed">
              <i className="ri-information-line mr-1" />
              说明：当前放映数据为前端演示示例。后续可接入实时排期接口，实现场次与票务的自动同步。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}